import React, { useState, useEffect } from 'react';
import { User } from '../../types/user';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { ArrowLeft, Save, X } from 'lucide-react';
import { useToast } from '../ui/use-toast';
import { userService } from '../../services/userService';
import { useUser } from '../../contexts/UserContext';

interface EditUserProps {
  user: User;
  onBack: () => void;
  onUserUpdated: (updatedUser: User) => void;
}

const EditUser: React.FC<EditUserProps> = ({ user, onBack, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    name: user.name || '',
    location: user.location || '',
    isActive: user.isActive,
    trainerCode: user.trainerCode || '',
    sellerType: user.sellerType || ''
  });
  const [loading, setLoading] = useState(false);
  const [trainers, setTrainers] = useState<any[]>([]);
  const [trainersLoading, setTrainersLoading] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useUser();

  useEffect(() => {
    fetchTrainers();
  }, []);

  const fetchTrainers = async () => {
    try {
      setTrainersLoading(true);
      const trainersData = await userService.getTrainers();
      setTrainers(trainersData);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast({
        title: "Error",
        description: "Failed to fetch trainers",
        variant: "destructive",
      });
    } finally {
      setTrainersLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const updatedUser = await userService.updateUser(user.id, {
        name: formData.name.trim(),
        location: formData.location.trim(),
        isActive: formData.isActive,
        trainerCode: formData.trainerCode,
        sellerType: formData.sellerType
      });

      toast({
        title: "Success",
        description: "User updated successfully",
      });

      onUserUpdated(updatedUser);
      onBack();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <CardTitle>Edit User</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">User Information</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={user.username}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter user name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    value={user.role}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sellerType">Seller Type</Label>
                  <select
                    id="sellerType"
                    value={formData.sellerType}
                    onChange={(e) => handleInputChange('sellerType', e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="zonal">zonal</option>
                    <option value="small">small</option>
                    <option value="top">top</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Additional Details</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Enter location"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currentBelt">Current Belt</Label>
                  <Input
                    id="currentBelt"
                    value={user.currentBelt}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="distributorCode">Distributor Code</Label>
                  <Input
                    id="distributorCode"
                    value={user.distributorCode}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trainerCode">Assign Trainer</Label>
                  <select
                    id="trainerCode"
                    value={formData.trainerCode}
                    onChange={(e) => handleInputChange('trainerCode', e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={trainersLoading}
                  >
                    <option value="">Select Trainer</option>
                    {trainers
                      .filter(trainer => trainer.distributorCode === user.distributorCode)
                      .map((trainer) => (
                        <option key={trainer.id} value={trainer.username}>
                          {trainer.username} ({trainer.name})
                        </option>
                      ))}
                  </select>
                  {trainersLoading && (
                    <p className="text-xs text-gray-500">Loading trainers...</p>
                  )}
                  {!trainersLoading && trainers.filter(trainer => trainer.distributorCode === user.distributorCode).length === 0 && (
                    <p className="text-xs text-gray-500">No trainers available for this distributor code</p>
                  )}
                </div>
              </div>
            </div>

            {currentUser?.role !== user.role && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Account Status</h3>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => handleInputChange('isActive', checked)}
                  />
                  <Label htmlFor="isActive">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </Label>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={loading}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
              >
                <Save className="mr-2 h-4 w-4" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditUser; 